import { useEffect, useState } from 'react';
import { api } from '../services/api';
import { EVENT_DIRECTIONS } from '../constants/directions';

/**
 * Source of truth for the chip selectors that drive direction multi-select
 * across master creator modals and public filters.
 *
 * Returns the canonical list as the BE currently advertises it via
 * GET /api/content/directions. The hardcoded EVENT_DIRECTIONS constant is
 * used as a synchronous bootstrap so the first render isn't empty, and as
 * an offline / fetch-fail fallback. A module-level cache shares the result
 * across components so each mount doesn't re-hit the network.
 *
 * Tolerant helpers (parseDirections, serializeDirections, DIRECTION_SET)
 * still key off the constant — those are for filtering existing data and
 * silently drop unknowns, so they don't need to track the live list.
 */

let cached: readonly string[] | null = null;
let inflight: Promise<readonly string[]> | null = null;

async function loadOnce(): Promise<readonly string[]> {
  if (cached) return cached;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const list = await api.getContentDirections();
      if (Array.isArray(list) && list.length > 0) {
        cached = list;
        return list;
      }
    } catch (err) {
      console.warn('Failed to load directions from backend, using fallback:', err);
    }
    cached = EVENT_DIRECTIONS;
    return EVENT_DIRECTIONS;
  })();
  return inflight;
}

export function useDirections(): readonly string[] {
  const [list, setList] = useState<readonly string[]>(cached ?? EVENT_DIRECTIONS);

  useEffect(() => {
    let mounted = true;
    loadOnce().then((next) => {
      if (mounted && next !== list) setList(next);
    });
    return () => { mounted = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return list;
}
